import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

/**
 * StaticMapBox
 * Mapbox GL을 사용한 정적 지도 컴포넌트
 *
 * Props:
 * @param {number} lat - 위도
 * @param {number} lng - 경도
 * @param {number} zoom - 줌 레벨 (기본: 14)
 * @param {number} height - 지도 높이 (기본: 200)
 * @param {boolean} showMarker - 마커 표시 여부 (기본: true)
 * @param {string} markerColor - 마커 색상 (기본: primary.main)
 * @param {object} sx - 추가 스타일
 */
const StaticMapBox = ({
  lat,
  lng,
  zoom = 14,
  height = 200,
  showMarker = true,
  markerColor,
  sx = {}
}) => {
  const theme = useTheme();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const containerEl = mapContainerRef.current;
    let mapboxglModule = null;

    const initMap = async () => {
      try {
        if (!containerEl || typeof lat !== 'number' || typeof lng !== 'number') {
          return;
        }

        const mod = await import('mapbox-gl');
        if (!isMounted) return;
        mapboxglModule = mod.default || mod;
        try { window.mapboxgl = mapboxglModule; } catch (_) { }

        // Get Mapbox token (same priority as ThreadsCafeMapSection)
        const runtimeToken =
          (typeof window !== 'undefined' && (window.REACT_APP_MAPBOX_TOKEN || window.MAPBOX_TOKEN)) ||
          (typeof localStorage !== 'undefined' && localStorage.getItem('MAPBOX_TOKEN')) ||
          process.env.REACT_APP_MAPBOX_TOKEN ||
          '';

        mapboxglModule.accessToken = runtimeToken;
        if (!runtimeToken) {
          console.warn('Mapbox token not found for StaticMapBox');
          return;
        }

        // Clean up existing map
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }

        // Use same style URL as ThreadsCafeMapSection with cache busting
        const baseStyleUrl = 'mapbox://styles/groovelb/cljxxcngj000m01rjfwx7dd47';
        const styleUrl = `${baseStyleUrl}?t=${Date.now()}`;

        // Create static map
        mapRef.current = new mapboxglModule.Map({
          container: containerEl,
          style: styleUrl,
          center: [lng, lat],
          zoom: zoom,
          attributionControl: false,
          // Disable all interactions for static map
          interactive: false,
          scrollZoom: false,
          boxZoom: false,
          dragRotate: false,
          dragPan: false,
          keyboard: false,
          doubleClickZoom: false,
          touchZoomRotate: false
        });

        // Add marker if requested
        if (showMarker) {
          mapRef.current.on('load', () => {
            if (!isMounted) return;

            // Create marker element similar to ThreadsCafeMapSection
            const wrapper = document.createElement('div');
            wrapper.style.display = 'flex';
            wrapper.style.flexDirection = 'column';
            wrapper.style.alignItems = 'center';
            wrapper.style.transform = 'translateY(0)';
            wrapper.style.pointerEvents = 'none'; // Disable interaction for static map

            const circle = document.createElement('div');
            circle.style.width = '28px';
            circle.style.height = '28px';
            circle.style.borderRadius = '50%';
            circle.style.display = 'flex';
            circle.style.alignItems = 'center';
            circle.style.justifyContent = 'center';
            circle.style.border = '1px solid transparent';
            circle.style.background = markerColor || theme.palette.primary.main;
            circle.style.color = '#fff';

            // Add coffee icon (same as ThreadsCafeMapSection)
            circle.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 8h14v6a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
                <path d="M16 8h2a3 3 0 1 1 0 6h-2" />
                <path d="M7 2v2" /><path d="M11 2v2" /><path d="M15 2v2" />
              </svg>
            `;

            wrapper.appendChild(circle);

            new mapboxglModule.Marker({ element: wrapper, anchor: 'bottom' })
              .setLngLat([lng, lat])
              .addTo(mapRef.current);
          });
        }

      } catch (error) {
        console.error('Failed to load StaticMapBox:', error);
      }
    };

    initMap();

    return () => {
      isMounted = false;
      if (mapRef.current) {
        try {
          mapRef.current.remove();
          mapRef.current = null;
        } catch (_) { }
      }
    };
  }, [lat, lng, zoom, showMarker, markerColor, theme]);

  return (
    <Box
      ref={mapContainerRef}
      sx={{
        width: '100%',
        height: height,
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'grey.100',
        ...sx
      }}
    />
  );
};

export default StaticMapBox;